import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Upload, ArrowLeft, BookOpen, FileText, Award, ExternalLink, ListChecks, Play, Loader2, BarChart3, Package, Download as DownloadIcon } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const api = {
    token: '',
    headers() { return { 'X-Admin-Token': api.token }; },
    get: (p) => axios.get(`${API}/api${p}`, { headers: api.headers() }),
    post: (p, body) => axios.post(`${API}/api${p}`, body, { headers: api.headers() }),
    put: (p, body) => axios.put(`${API}/api${p}`, body, { headers: api.headers() }),
    del: (p) => axios.delete(`${API}/api${p}`, { headers: api.headers() }),
};

const errMsg = (e) => e?.response?.data?.detail || e.message || 'Errore';

const STATUSES = [
    { v: 'available', l: 'Disponibile' },
    { v: 'coming', l: 'In arrivo' },
    { v: 'reserved', l: 'Riservato' },
    { v: 'hidden', l: 'Nascosto' },
];

const KINDS = [
    { v: 'pdf', l: 'PDF', icon: FileText },
    { v: 'zip', l: 'ZIP / Pacchetto scaricabile', icon: Package },
    { v: 'excel', l: 'File Excel', icon: FileText },
    { v: 'image', l: 'Immagine / documento', icon: FileText },
    { v: 'link', l: 'Link esterno', icon: ExternalLink },
    { v: 'video', l: 'Video / embed', icon: Play },
    { v: 'checklist', l: 'Checklist / testo', icon: ListChecks },
    { v: 'quiz', l: 'Quiz', icon: Award },
];

export default function ExtraBooksPanel({ token }) {
    api.token = token;
    const [selectedBook, setSelectedBook] = useState(null);
    const [view, setView] = useState('books');
    const [books, setBooks] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadBooks = useCallback(async () => {
        try {
            const r = await api.get('/admin/extra/books');
            setBooks(r.data.items || []);
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadBooks(); }, [loadBooks]);

    if (selectedBook) {
        return (
            <div className="pvx-public">
                <BookDetailPanel book={selectedBook} onBack={() => setSelectedBook(null)} />
            </div>
        );
    }

    return (
        <div className="pvx-public">
            <div className="flex items-start justify-between mb-6">
                <div>
                    <div className="pvx-eyebrow text-pvx-primary mb-2">Amministrazione</div>
                    <h1 className="pvx-serif text-4xl tracking-tight mb-2">Extra libri</h1>
                    <p className="text-[13.5px] text-pvx-textmuted max-w-xl">
                        Gestisci i libri visibili su <span className="pvx-mono">/extra</span>, i loro contenuti extra e l'elenco dei lettori registrati.
                    </p>
                </div>
                {view === 'books' && <BookEditor onSaved={loadBooks} />}
            </div>

            <div className="inline-flex items-center gap-1 p-1 rounded-[10px] bg-pvx-surface2 mb-8" data-testid="admin-extra-switcher">
                <button onClick={() => setView('books')} data-testid="admin-view-books"
                        className={`h-8 px-4 rounded-[8px] text-[12.5px] font-medium transition-colors ${view === 'books' ? 'bg-white shadow-sm text-pvx-textmain' : 'text-pvx-textmuted hover:text-pvx-textmain'}`}>
                    Libri
                </button>
                <button onClick={() => setView('readers')} data-testid="admin-view-readers"
                        className={`h-8 px-4 rounded-[8px] text-[12.5px] font-medium transition-colors ${view === 'readers' ? 'bg-white shadow-sm text-pvx-textmain' : 'text-pvx-textmuted hover:text-pvx-textmain'}`}>
                    Lettori
                </button>
            </div>

            {view === 'readers' ? (
                <ReadersSection />
            ) : loading ? (
                <div className="text-pvx-textmuted text-[13px]"><Loader2 size={16} strokeWidth={1.5} className="inline animate-spin mr-2" />Carico…</div>
            ) : books.length === 0 ? (
                <div className="pvx-card p-10 text-center">
                    <BookOpen size={26} strokeWidth={1.5} className="mx-auto text-pvx-textsubtle mb-3" />
                    <div className="text-[14px] font-medium mb-1">Nessun libro ancora</div>
                    <div className="text-[12.5px] text-pvx-textsubtle mb-4">Crea il primo libro per iniziare a caricare i contenuti extra.</div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="admin-books-grid">
                    {books.map((b) => (
                        <div key={b.id} className="pvx-card p-5" data-testid={`admin-book-${b.id}`}>
                            <div className="flex items-start justify-between gap-3 mb-3">
                                <div className="flex items-start gap-3 min-w-0">
                                    {b.cover_path && (
                                        <img src={`${API}/api/public/extra/cover/${b.id}`} alt=""
                                             className="h-14 w-10 object-contain shrink-0 rounded-[4px] bg-[#F4F6FA]"
                                             onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                    )}
                                    <div className="min-w-0">
                                        <div className="text-[14px] font-medium truncate">{b.title}</div>
                                        {b.subtitle && <div className="text-[12px] text-pvx-textmuted truncate">{b.subtitle}</div>}
                                    </div>
                                </div>
                                <StatusBadge s={b.status} />
                            </div>
                            {b.short_description && (
                                <p className="text-[12.5px] text-pvx-textsubtle line-clamp-2 mb-4">{b.short_description}</p>
                            )}
                            <div className="flex flex-wrap items-center gap-2">
                                <button onClick={() => setSelectedBook(b)}
                                        data-testid={`admin-book-open-${b.id}`}
                                        className="h-8 px-3 rounded-[10px] bg-pvx-primary text-white text-[12.5px] font-medium hover:opacity-90 transition-opacity">
                                    Apri contenuti
                                </button>
                                <BookEditor book={b} onSaved={loadBooks} />
                                <DeleteBookButton book={b} onDone={loadBooks} />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function ReadersSection() {
    const [readers, setReaders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [prof, setProf] = useState('');

    useEffect(() => {
        api.get('/admin/extra/readers')
            .then(r => setReaders(r.data.items || []))
            .catch(e => toast.error(errMsg(e)))
            .finally(() => setLoading(false));
    }, []);

    const professions = [...new Set(readers.map(r => r.profession).filter(Boolean))].sort();
    const filtered = readers.filter(r => {
        if (prof && r.profession !== prof) return false;
        if (q) {
            const s = `${r.first_name || ''} ${r.last_name || ''} ${r.email || ''} ${r.city || ''}`.toLowerCase();
            if (!s.includes(q.toLowerCase())) return false;
        }
        return true;
    });

    const fmtDate = (iso) => {
        if (!iso) return '—';
        try {
            const d = new Date(iso);
            return d.toLocaleDateString('it-IT') + ' ' + d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
        } catch { return iso; }
    };
    const fmtBirth = (iso) => {
        if (!iso) return '—';
        const [y, m, d] = iso.split('-');
        return d && m && y ? `${d}/${m}/${y}` : iso;
    };

    const exportCsv = () => {
        const head = ['Nome', 'Cognome', 'Email', 'Cellulare', 'Città di residenza', 'Data di nascita', 'Professione', 'Libro di provenienza', 'Consenso comunicazioni', 'Registrato il'];
        const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const rows = filtered.map(r => [
            r.first_name, r.last_name, r.email, r.phone, r.city, fmtBirth(r.birth_date),
            r.profession, r.from_book_title || '—', r.marketing_consent ? 'Sì' : 'No', fmtDate(r.created_at),
        ].map(esc).join(';'));
        const csv = '\uFEFF' + head.map(esc).join(';') + '\n' + rows.join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `lettori-extra-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success('CSV esportato');
    };

    return (
        <div data-testid="admin-readers-section">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex flex-wrap items-center gap-2">
                    <input
                        value={q}
                        onChange={e => setQ(e.target.value)}
                        placeholder="Cerca nome, email, città…"
                        data-testid="admin-readers-search"
                        className="h-8 w-56 px-3 rounded-[10px] border border-pvx-border/[0.15] bg-white focus:border-pvx-primary/50 text-[12.5px] outline-none transition-colors"
                    />
                    <select
                        value={prof}
                        onChange={e => setProf(e.target.value)}
                        data-testid="admin-readers-filter-profession"
                        className="h-8 px-2.5 rounded-[10px] border border-pvx-border/[0.15] bg-white focus:border-pvx-primary/50 text-[12.5px] outline-none transition-colors"
                    >
                        <option value="">Tutte le professioni</option>
                        {professions.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <div className="text-[12.5px] text-pvx-textmuted" data-testid="admin-readers-count">
                        <b className="text-pvx-textmain">{filtered.length}</b>{filtered.length !== readers.length ? ` di ${readers.length}` : ''} {readers.length === 1 ? 'lettore' : 'lettori'}
                    </div>
                </div>
                <button onClick={exportCsv} disabled={filtered.length === 0}
                        data-testid="admin-readers-export"
                        className="h-8 px-3 rounded-[10px] bg-pvx-primary text-white text-[12.5px] font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5 transition-opacity">
                    <DownloadIcon size={13} strokeWidth={1.75} /> Esporta CSV
                </button>
            </div>

            {loading ? (
                <div className="text-pvx-textmuted text-[13px]"><Loader2 size={14} strokeWidth={1.5} className="inline animate-spin mr-2" />Carico…</div>
            ) : filtered.length === 0 ? (
                <div className="pvx-card p-8 text-center text-pvx-textmuted text-[13px]" data-testid="admin-readers-empty">
                    {readers.length === 0 ? 'Nessun lettore registrato al momento.' : 'Nessun lettore corrisponde alla ricerca.'}
                </div>
            ) : (
                <div className="pvx-card overflow-x-auto">
                    <table className="w-full text-left" data-testid="admin-readers-table">
                        <thead>
                            <tr className="border-b border-pvx-border/[0.08]">
                                {['Lettore', 'Contatti', 'Città', 'Nascita', 'Professione', 'Libro di provenienza', 'Registrato il'].map(h => (
                                    <th key={h} className="px-4 py-3 pvx-eyebrow whitespace-nowrap">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(r => (
                                <tr key={r.id} className="border-b border-pvx-border/[0.05] last:border-0 hover:bg-pvx-surface2/50 transition-colors" data-testid={`admin-reader-${r.id}`}>
                                    <td className="px-4 py-3 text-[13px] font-medium whitespace-nowrap">{r.first_name} {r.last_name}</td>
                                    <td className="px-4 py-3 text-[12px] text-pvx-textmuted">
                                        <div>{r.email}</div>
                                        <div className="text-pvx-textsubtle">{r.phone}</div>
                                    </td>
                                    <td className="px-4 py-3 text-[12.5px] text-pvx-textmuted whitespace-nowrap">{r.city || '—'}</td>
                                    <td className="px-4 py-3 text-[12.5px] text-pvx-textmuted whitespace-nowrap">{fmtBirth(r.birth_date)}</td>
                                    <td className="px-4 py-3 text-[12.5px] whitespace-nowrap">
                                        <span className="px-2 py-0.5 rounded-full bg-pvx-primary/10 text-pvx-primary text-[11px]">{r.profession || '—'}</span>
                                    </td>
                                    <td className="px-4 py-3 text-[12.5px] text-pvx-textmuted max-w-[220px] truncate">{r.from_book_title || '—'}</td>
                                    <td className="px-4 py-3 text-[12px] text-pvx-textsubtle whitespace-nowrap">{fmtDate(r.created_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function StatusBadge({ s }) {
    const l = STATUSES.find(x => x.v === s)?.l || s;
    const tone = s === 'available' ? 'text-pvx-success bg-pvx-success/10' :
                 s === 'coming' ? 'text-pvx-warning bg-pvx-warning/10' :
                 s === 'hidden' ? 'text-pvx-textsubtle bg-pvx-surface2' :
                 'text-pvx-primary bg-pvx-primary/10';
    return <span className={`text-[10.5px] px-2 py-0.5 rounded-full ${tone}`}>{l}</span>;
}

function BookEditor({ book, onSaved }) {
    const [open, setOpen] = useState(false);
    const isEdit = !!book;

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                data-testid={isEdit ? `admin-book-edit-${book.id}` : 'admin-book-new'}
                className={`h-8 px-3 rounded-[10px] text-[12.5px] font-medium flex items-center gap-1.5 transition-colors ${
                    isEdit ? 'border border-pvx-border hover:bg-pvx-surface2' : 'bg-pvx-primary text-white hover:opacity-90'
                }`}>
                {isEdit ? <><Pencil size={12.5} strokeWidth={1.5} /> Modifica</> : <><Plus size={13} strokeWidth={1.5} /> Nuovo libro</>}
            </button>
            {open && <BookForm book={book} onClose={() => setOpen(false)} onSaved={onSaved} />}
        </>
    );
}

function BookForm({ book, onClose, onSaved }) {
    const [form, setForm] = useState({
        title: book?.title || '', subtitle: book?.subtitle || '',
        short_description: book?.short_description || '',
        long_description: book?.long_description || '',
        author_name: book?.author_name || '',
        status: book?.status || 'available',
        cover_path: book?.cover_path || null,
        sort_order: book?.sort_order || 0,
        purchase_url: book?.purchase_url || '',
        purchase_cta_text: book?.purchase_cta_text || '',
    });
    const [busy, setBusy] = useState(false);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const uploadCover = async (file) => {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('kind', 'cover');
        setBusy(true);
        try {
            const r = await api.post('/admin/extra/upload', fd);
            set('cover_path', r.data.storage_path);
            toast.success('Copertina caricata');
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setBusy(false);
        }
    };

    const save = async () => {
        if (!form.title.trim()) { toast.error('Titolo obbligatorio'); return; }
        setBusy(true);
        try {
            if (book) {
                await api.put(`/admin/extra/books/${book.id}`, form);
            } else {
                await api.post('/admin/extra/books', form);
            }
            toast.success(book ? 'Libro aggiornato' : 'Libro creato');
            onSaved?.();
            onClose();
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8 p-6" data-testid="admin-book-form">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-[16px] font-medium">{book ? 'Modifica libro' : 'Nuovo libro'}</h2>
                    <button onClick={onClose} className="text-pvx-textmuted hover:text-pvx-textmain text-[13px]">Chiudi</button>
                </div>
                <div className="space-y-4">
                    <Field label="Titolo" required>
                        <input required value={form.title} onChange={e => set('title', e.target.value)}
                               data-testid="admin-book-title" className={inputCls} />
                    </Field>
                    <Field label="Sottotitolo">
                        <input value={form.subtitle} onChange={e => set('subtitle', e.target.value)}
                               data-testid="admin-book-subtitle" className={inputCls} />
                    </Field>
                    <Field label="Autore" hint="nome mostrato ai lettori">
                        <input value={form.author_name} onChange={e => set('author_name', e.target.value)}
                               data-testid="admin-book-author" className={inputCls} placeholder="es. Piera Giordano" />
                    </Field>
                    <Field label="Descrizione breve" hint="visibile nella card">
                        <textarea rows={2} value={form.short_description} onChange={e => set('short_description', e.target.value)}
                               data-testid="admin-book-short" className={inputCls} />
                    </Field>
                    <Field label="Descrizione lunga" hint="visibile nella pagina dettaglio libro">
                        <textarea rows={4} value={form.long_description} onChange={e => set('long_description', e.target.value)}
                               data-testid="admin-book-long" className={inputCls} />
                    </Field>

                    <div className="border border-pvx-border rounded-xl p-4 bg-pvx-primary/[0.03] space-y-3">
                        <div className="pvx-eyebrow text-pvx-primary flex items-center gap-1.5">
                            <span>Link acquisto libro</span>
                            <span className="text-pvx-textsubtle normal-case tracking-normal font-normal text-[10.5px]">· mostrato senza obbligo di login</span>
                        </div>
                        <Field label="Link acquisto (URL)" hint="Amazon, sito editore, ecc.">
                            <input value={form.purchase_url} onChange={e => set('purchase_url', e.target.value)}
                                   data-testid="admin-book-purchase-url" className={inputCls}
                                   placeholder="https://www.amazon.it/dp/..." />
                        </Field>
                        <Field label="Testo pulsante" hint="lascia vuoto per usare «Acquista il libro»">
                            <input value={form.purchase_cta_text} onChange={e => set('purchase_cta_text', e.target.value)}
                                   data-testid="admin-book-purchase-cta" className={inputCls}
                                   placeholder="es. Compra su Amazon" />
                        </Field>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Stato">
                            <select value={form.status} onChange={e => set('status', e.target.value)}
                                    data-testid="admin-book-status" className={inputCls}>
                                {STATUSES.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
                            </select>
                        </Field>
                        <Field label="Ordine" hint="più basso = prima">
                            <input type="number" value={form.sort_order} onChange={e => set('sort_order', parseInt(e.target.value) || 0)}
                                   data-testid="admin-book-sort" className={inputCls} />
                        </Field>
                    </div>
                    <Field label="Copertina (PNG consigliato senza sfondo)">
                        {form.cover_path && (
                            <div className="text-[11.5px] text-pvx-success mb-2 pvx-mono truncate">✓ {form.cover_path}</div>
                        )}
                        <label className="inline-flex items-center gap-2 h-9 px-3 rounded-[10px] border border-dashed border-pvx-border hover:bg-pvx-surface2 cursor-pointer text-[12.5px] transition-colors">
                            <Upload size={13.5} strokeWidth={1.5} />
                            Carica copertina
                            <input type="file" accept="image/*" className="hidden"
                                   data-testid="admin-book-cover"
                                   onChange={e => e.target.files?.[0] && uploadCover(e.target.files[0])} />
                        </label>
                    </Field>
                </div>
                <div className="mt-6 flex justify-end gap-2">
                    <button onClick={onClose} data-testid="admin-book-cancel"
                            className="h-9 px-4 rounded-[10px] border border-pvx-border hover:bg-pvx-surface2 text-[13px]">Annulla</button>
                    <button onClick={save} disabled={busy} data-testid="admin-book-save"
                            className="h-9 px-4 rounded-[10px] bg-pvx-primary text-white hover:opacity-90 disabled:opacity-60 text-[13px] font-medium">
                        {busy ? 'Salvataggio…' : 'Salva'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function DeleteBookButton({ book, onDone }) {
    const del = async () => {
        if (!window.confirm(`Eliminare «${book.title}»? Verranno rimossi anche tutti i contenuti collegati.`)) return;
        try {
            await api.del(`/admin/extra/books/${book.id}`);
            toast.success('Libro eliminato');
            onDone?.();
        } catch (e) {
            toast.error(errMsg(e));
        }
    };
    return (
        <button onClick={del} data-testid={`admin-book-delete-${book.id}`}
                className="h-8 px-2.5 rounded-[10px] border border-pvx-border hover:bg-pvx-critical/10 hover:border-pvx-critical/40 hover:text-pvx-critical text-[12.5px] transition-colors">
            <Trash2 size={12.5} strokeWidth={1.5} />
        </button>
    );
}

function BookDetailPanel({ book, onBack }) {
    const [contents, setContents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);

    const loadContents = useCallback(async () => {
        try {
            const r = await api.get(`/admin/extra/books/${book.id}/contents`);
            setContents(r.data.items || []);
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setLoading(false);
        }
    }, [book.id]);

    useEffect(() => {
        loadContents();
        api.get(`/admin/extra/books/${book.id}/stats`).then(r => setStats(r.data)).catch(() => {});
    }, [book.id, loadContents]);

    return (
        <div>
            <button onClick={onBack} data-testid="admin-book-back"
                    className="inline-flex items-center gap-1.5 text-[13px] text-pvx-textmuted hover:text-pvx-textmain mb-6 transition-colors">
                <ArrowLeft size={14} strokeWidth={1.5} /> Torna ai libri
            </button>
            <div className="mb-8">
                <h1 className="pvx-serif text-3xl tracking-tight mb-1">{book.title}</h1>
                {book.subtitle && <div className="text-[13.5px] text-pvx-textmuted">{book.subtitle}</div>}
            </div>

            {stats && (
                <div className="pvx-card p-5 mb-6" data-testid="book-stats">
                    <div className="flex items-center gap-2 text-[12.5px] font-medium text-pvx-primary mb-3">
                        <BarChart3 size={14} strokeWidth={1.5} /> Analytics del libro
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <Stat label="Lettori raggiunti" value={stats.readers_reached} />
                        <Stat label="Registrazioni" value={stats.registrations} />
                        <Stat label="Aperture" value={stats.views} />
                        <Stat label="Download" value={stats.downloads} />
                        <Stat label="Quiz completati" value={`${stats.quiz_completed}/${stats.quiz_started}`} />
                    </div>
                    {stats.folders?.length > 0 && (
                        <div className="mt-5 pt-4 border-t border-pvx-border/[0.08]" data-testid="folder-stats">
                            <div className="pvx-eyebrow mb-3">Cartelle più scaricate</div>
                            <div className="space-y-2.5">
                                {stats.folders.map(f => {
                                    const max = Math.max(...stats.folders.map(x => x.downloads), 1);
                                    return (
                                        <div key={f.folder} data-testid={`folder-stat-${f.folder}`} className="flex items-center gap-3">
                                            <div className="w-44 shrink-0 truncate text-[12.5px] font-medium">{f.folder}</div>
                                            <div className="flex-1 h-2 rounded-full bg-pvx-surface2 overflow-hidden">
                                                <div className="h-full rounded-full bg-pvx-primary transition-all" style={{ width: `${Math.round((f.downloads / max) * 100)}%` }} />
                                            </div>
                                            <div className="w-56 shrink-0 text-right text-[11.5px] text-pvx-textmuted">
                                                <b className="text-pvx-textmain pvx-mono">{f.downloads}</b> download file · {f.zip_downloads} cartelle ZIP · {f.files} file
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="flex items-center justify-between mb-4">
                <h2 className="text-[15px] font-medium">Contenuti del libro</h2>
                <ContentEditor bookId={book.id} onSaved={loadContents} />
            </div>

            {loading ? (
                <div className="text-pvx-textmuted text-[13px]"><Loader2 size={14} strokeWidth={1.5} className="inline animate-spin mr-2" />Carico…</div>
            ) : contents.length === 0 ? (
                <div className="pvx-card p-8 text-center text-pvx-textmuted text-[13px]">
                    Nessun contenuto ancora. Usa «Nuovo contenuto» per iniziare.
                </div>
            ) : (
                <div className="space-y-3" data-testid="admin-contents-list">
                    {contents.map(c => (
                        <ContentRow key={c.id} c={c} bookId={book.id} onSaved={loadContents} />
                    ))}
                </div>
            )}
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div>
            <div className="pvx-mono text-2xl font-medium">{value ?? 0}</div>
            <div className="text-[11px] text-pvx-textsubtle uppercase tracking-wider mt-0.5">{label}</div>
        </div>
    );
}

function ContentEditor({ bookId, content, onSaved }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <button onClick={() => setOpen(true)}
                    data-testid={content ? `admin-content-edit-${content.id}` : 'admin-content-new'}
                    className={`h-8 px-3 rounded-[10px] text-[12.5px] font-medium flex items-center gap-1.5 transition-colors ${
                        content ? 'border border-pvx-border hover:bg-pvx-surface2' : 'bg-pvx-primary text-white hover:opacity-90'
                    }`}>
                {content ? <><Pencil size={12} strokeWidth={1.5} /> Modifica</> : <><Plus size={13} strokeWidth={1.5} /> Nuovo contenuto</>}
            </button>
            {open && <ContentForm bookId={bookId} content={content} onClose={() => setOpen(false)} onSaved={onSaved} />}
        </>
    );
}

function ContentForm({ bookId, content, onClose, onSaved }) {
    const [form, setForm] = useState({
        book_id: bookId,
        title: content?.title || '',
        kind: content?.kind || 'pdf',
        folder: content?.folder || '',
        storage_path: content?.storage_path || null,
        original_filename: content?.original_filename || null,
        mime_type: content?.mime_type || null,
        url: content?.url || '',
        content_text: content?.content_text || '',
        quiz_id: content?.quiz_id || null,
        sort_order: content?.sort_order || 0,
    });
    const [quizForm, setQuizForm] = useState({
        title: '', description: '', passing_score: 60,
        questions: [{ q: '', options: ['', '', '', ''], correct_index: 0, explain: '' }],
    });
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const uploadFile = async (file) => {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('kind', form.kind);
        setBusy(true);
        try {
            const r = await api.post('/admin/extra/upload', fd);
            const data = r.data;
            set('storage_path', data.storage_path);
            set('original_filename', data.original_filename);
            set('mime_type', data.mime_type);
            toast.success(`File caricato (${(data.size / 1024).toFixed(0)} KB)`);
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setBusy(false);
        }
    };

    const save = async () => {
        if (!form.title.trim()) { toast.error('Titolo obbligatorio'); return; }
        setBusy(true);
        try {
            let payload = { ...form };
            if (form.kind === 'quiz' && !form.quiz_id) {
                const validQs = quizForm.questions.filter(q => q.q && q.options.every(o => o));
                if (validQs.length === 0) throw new Error('Aggiungi almeno una domanda valida');
                const quiz = await api.post('/admin/extra/quizzes', {
                    book_id: bookId,
                    title: quizForm.title || form.title,
                    description: quizForm.description,
                    passing_score: quizForm.passing_score,
                    questions: validQs,
                }).then(r => r.data);
                payload.quiz_id = quiz.id;
            }
            if (content) {
                await api.put(`/admin/extra/contents/${content.id}`, payload);
            } else {
                await api.post('/admin/extra/contents', payload);
            }
            toast.success(content ? 'Contenuto aggiornato' : 'Contenuto creato');
            onSaved?.();
            onClose();
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setBusy(false);
        }
    };

    const isUploadable = ['pdf', 'zip', 'image', 'excel', 'video'].includes(form.kind);
    const hasUrl = ['link', 'video', 'embed'].includes(form.kind);
    const isText = ['checklist'].includes(form.kind);
    const isQuiz = form.kind === 'quiz';

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8 p-6" data-testid="admin-content-form">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-[16px] font-medium">{content ? 'Modifica contenuto' : 'Nuovo contenuto'}</h2>
                    <button onClick={onClose} className="text-pvx-textmuted hover:text-pvx-textmain text-[13px]">Chiudi</button>
                </div>
                <div className="space-y-4">
                    <Field label="Titolo" required>
                        <input required value={form.title} onChange={e => set('title', e.target.value)}
                               data-testid="admin-content-title" className={inputCls} />
                    </Field>
                    <Field label="Tipologia" required>
                        <select value={form.kind} onChange={e => set('kind', e.target.value)}
                                data-testid="admin-content-kind" className={inputCls}>
                            {KINDS.map(k => <option key={k.v} value={k.v}>{k.l}</option>)}
                        </select>
                    </Field>
                    <Field label="Cartella / sezione" hint='opzionale · es. "File Excel", "Documenti PDF"'>
                        <input value={form.folder} onChange={e => set('folder', e.target.value)}
                               data-testid="admin-content-folder" className={inputCls}
                               placeholder="Lascia vuoto per mostrarlo senza cartella" />
                    </Field>
                    {isUploadable && (
                        <Field label="File">
                            {form.storage_path && (
                                <div className="text-[11.5px] text-pvx-success mb-2 pvx-mono truncate">✓ {form.original_filename || form.storage_path}</div>
                            )}
                            <label className="inline-flex items-center gap-2 h-9 px-3 rounded-[10px] border border-dashed border-pvx-border hover:bg-pvx-surface2 cursor-pointer text-[12.5px] transition-colors">
                                <Upload size={13.5} strokeWidth={1.5} /> Carica file
                                <input type="file" className="hidden"
                                       data-testid="admin-content-file"
                                       onChange={e => e.target.files?.[0] && uploadFile(e.target.files[0])} />
                            </label>
                        </Field>
                    )}
                    {hasUrl && (
                        <Field label={form.kind === 'link' ? 'URL' : 'URL video / embed'}>
                            <input value={form.url} onChange={e => set('url', e.target.value)}
                                   data-testid="admin-content-url" className={inputCls}
                                   placeholder={form.kind === 'link' ? 'https://...' : 'https://www.youtube.com/embed/...'} />
                        </Field>
                    )}
                    {(isText || form.content_text) && (
                        <Field label="Testo / checklist">
                            <textarea rows={4} value={form.content_text} onChange={e => set('content_text', e.target.value)}
                                      data-testid="admin-content-text" className={inputCls} />
                        </Field>
                    )}
                    {isQuiz && !content && (
                        <QuizBuilder quizForm={quizForm} setQuizForm={setQuizForm} />
                    )}
                    <Field label="Ordine">
                        <input type="number" value={form.sort_order} onChange={e => set('sort_order', parseInt(e.target.value) || 0)}
                               data-testid="admin-content-sort" className={inputCls} />
                    </Field>
                </div>
                <div className="mt-6 flex justify-end gap-2">
                    <button onClick={onClose} data-testid="admin-content-cancel"
                            className="h-9 px-4 rounded-[10px] border border-pvx-border hover:bg-pvx-surface2 text-[13px]">Annulla</button>
                    <button onClick={save} disabled={busy} data-testid="admin-content-save"
                            className="h-9 px-4 rounded-[10px] bg-pvx-primary text-white hover:opacity-90 disabled:opacity-60 text-[13px] font-medium">
                        {busy ? 'Salvataggio…' : 'Salva'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function QuizBuilder({ quizForm, setQuizForm }) {
    const setQ = (i, k, v) => setQuizForm(f => ({ ...f, questions: f.questions.map((q, j) => j === i ? { ...q, [k]: v } : q) }));
    const setOpt = (i, j, v) => setQuizForm(f => ({ ...f, questions: f.questions.map((q, k) => k === i ? { ...q, options: q.options.map((o, l) => l === j ? v : o) } : q) }));
    const addQ = () => setQuizForm(f => ({ ...f, questions: [...f.questions, { q: '', options: ['', '', '', ''], correct_index: 0, explain: '' }] }));
    const rmQ = (i) => setQuizForm(f => ({ ...f, questions: f.questions.filter((_, j) => j !== i) }));

    return (
        <div className="border border-pvx-border rounded-xl p-4 bg-pvx-surface2/40" data-testid="quiz-builder">
            <div className="pvx-eyebrow mb-3 text-pvx-primary">Domande del quiz</div>
            <div className="grid grid-cols-2 gap-3 mb-4">
                <Field label="Titolo quiz"><input value={quizForm.title} onChange={e => setQuizForm(f => ({ ...f, title: e.target.value }))} className={inputCls} data-testid="quiz-title" /></Field>
                <Field label="Punteggio minimo (%)"><input type="number" value={quizForm.passing_score} onChange={e => setQuizForm(f => ({ ...f, passing_score: parseInt(e.target.value) || 60 }))} className={inputCls} /></Field>
            </div>
            {quizForm.questions.map((qs, i) => (
                <div key={i} className="border-t border-pvx-border pt-3 mt-3 space-y-2" data-testid={`quiz-q-${i}`}>
                    <div className="flex items-center justify-between">
                        <div className="pvx-eyebrow">Domanda {i + 1}</div>
                        {quizForm.questions.length > 1 && (
                            <button onClick={() => rmQ(i)} className="text-pvx-critical text-[11.5px]">Rimuovi</button>
                        )}
                    </div>
                    <input placeholder="Testo domanda" value={qs.q} onChange={e => setQ(i, 'q', e.target.value)}
                           className={inputCls} data-testid={`quiz-q-${i}-text`} />
                    <div className="space-y-1.5">
                        {qs.options.map((opt, j) => (
                            <label key={j} className="flex items-center gap-2">
                                <input type="radio" name={`q${i}-correct`}
                                       checked={qs.correct_index === j}
                                       onChange={() => setQ(i, 'correct_index', j)}
                                       className="accent-pvx-primary"
                                       data-testid={`quiz-q-${i}-correct-${j}`} />
                                <input placeholder={`Opzione ${j + 1}`} value={opt}
                                       onChange={e => setOpt(i, j, e.target.value)}
                                       className={`${inputCls} flex-1`} data-testid={`quiz-q-${i}-opt-${j}`} />
                            </label>
                        ))}
                    </div>
                    <input placeholder="Spiegazione (opzionale)" value={qs.explain}
                           onChange={e => setQ(i, 'explain', e.target.value)}
                           className={inputCls} />
                </div>
            ))}
            <button onClick={addQ} data-testid="quiz-add-q"
                    className="mt-3 h-8 px-3 rounded-[10px] border border-pvx-border hover:bg-pvx-surface2 text-[12.5px] font-medium">
                + Aggiungi domanda
            </button>
        </div>
    );
}

function ContentRow({ c, bookId, onSaved }) {
    const Icon = KINDS.find(k => k.v === c.kind)?.icon || FileText;
    const del = async () => {
        if (!window.confirm(`Eliminare "${c.title}"?`)) return;
        try {
            await api.del(`/admin/extra/contents/${c.id}`);
            toast.success('Contenuto eliminato');
            onSaved?.();
        } catch (e) {
            toast.error(errMsg(e));
        }
    };
    return (
        <div className="pvx-card p-4 flex items-center gap-4" data-testid={`admin-content-${c.id}`}>
            <div className="h-9 w-9 rounded-[10px] bg-pvx-primary/[0.08] text-pvx-primary flex items-center justify-center">
                <Icon size={15} strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[13.5px] font-medium truncate">{c.title}</div>
                <div className="text-[11.5px] text-pvx-textsubtle truncate">
                    {c.kind}{c.folder ? ` · 📁 ${c.folder}` : ''} · {c.original_filename || c.url || (c.storage_path ? 'file caricato' : '—')}
                </div>
            </div>
            <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-pvx-surface2 text-[11px] text-pvx-textmuted shrink-0" title="Download totali dai lettori" data-testid={`admin-content-downloads-${c.id}`}>
                <DownloadIcon size={11} strokeWidth={1.75} /> {c.download_count ?? 0}
            </div>
            <ContentEditor bookId={bookId} content={c} onSaved={onSaved} />
            <button onClick={del} data-testid={`admin-content-delete-${c.id}`}
                    className="h-8 px-2.5 rounded-[10px] border border-pvx-border hover:bg-pvx-critical/10 hover:border-pvx-critical/40 hover:text-pvx-critical transition-colors">
                <Trash2 size={12.5} strokeWidth={1.5} />
            </button>
        </div>
    );
}

const inputCls = "w-full h-10 px-3 rounded-[10px] border border-pvx-border bg-pvx-surface/60 focus:bg-pvx-surface focus:border-pvx-primary/50 text-[13.5px] outline-none transition-colors";

function Field({ label, required, hint, children }) {
    return (
        <div>
            <label className="pvx-eyebrow mb-1.5 block flex items-baseline gap-1">
                <span>{label}{required && <span className="text-pvx-critical ml-0.5">*</span>}</span>
                {hint && <span className="text-pvx-textsubtle normal-case tracking-normal font-normal text-[10.5px]">· {hint}</span>}
            </label>
            {children}
        </div>
    );
}
